import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Data Toolkit dropdown: looks like the other inputs, opens a menu with an optional search box,
 * and supports the keyboard (↑ ↓ to move, Enter to pick, Esc to close).
 *
 * props:
 *  - options: [{ value, label, meta? }]  meta is shown muted on the right (e.g. a count)
 *  - value, onChange(value)
 *  - placeholder, disabled, searchable, searchPlaceholder, emptyText, id
 */
const DtSelect = ({
  options = [],
  value = "",
  onChange = () => {},
  placeholder = "Select",
  disabled = false,
  searchable = false,
  searchPlaceholder = "Search",
  emptyText = "Nothing matches",
  id
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find(o => o.value === value);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => String(o.label).toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => rootRef.current && !rootRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    const idx = Math.max(0, shown.findIndex(o => o.value === value));
    setActive(idx);
    setTimeout(() => (searchable ? searchRef.current?.focus() : listRef.current?.focus()), 0);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current?.children?.[active];
    el && el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const pick = option => {
    if (!option) return;
    onChange(option.value);
    close();
  };

  const onKeyDown = e => {
    if (!open) {
      if (["Enter", " ", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(i => Math.min(shown.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(i => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(shown[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  return (
    <div className={`dtSelect ${open ? "open" : ""} ${disabled ? "disabled" : ""}`} ref={rootRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        id={id}
        className="dtSelectTrigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className={`dtSelectValue ${selected ? "" : "placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        {selected?.meta != null && <span className="dtSelectMeta">{selected.meta}</span>}
        <svg className="dtSelectChevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
          <path d="m5 7.5 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="dtSelectMenu">
          {searchable && (
            <div className="dtSelectSearch">
              <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <path d="m13.2 13.2 3.3 3.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <input
                ref={searchRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
            </div>
          )}
          <ul className="dtSelectList" role="listbox" ref={listRef} tabIndex={-1}>
            {shown.length ? (
              shown.map((o, i) => (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={o.value === value}
                  className={`${i === active ? "active" : ""} ${o.value === value ? "selected" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => pick(o)}
                  title={o.label}
                >
                  <span className="dtSelectOptLabel">{o.label}</span>
                  {o.meta != null && <span className="dtSelectMeta">{o.meta}</span>}
                  {o.value === value && (
                    <svg className="dtSelectCheck" viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                      <path d="m5 10.5 3.2 3.2L15 7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </li>
              ))
            ) : (
              <li className="dtSelectEmpty">{emptyText}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DtSelect;
